import { useCallback, useEffect, useState } from 'react';
import type { GridApi, GridReadyEvent } from 'ag-grid-community';

import aguid from 'lib/aguid';

import useRowHighlighter from '../../../hooks/useRowHighlighter';
import useIsCompatibleComparison from '../../../hooks/useIsCompatableComparison';
import useRowData from './hooks/useRowData';
import useGridOptions from './hooks/useGridOptions';
import useChartDrillDowns from '../../../hooks/useChartDrillDowns';

const usePaceMatrixContent = ({
  gadget,
  gridApi,
  setGridApi,
}: {
  gadget: VisualisationDefinitions.PaceMatrix;
  gridApi?: GridApi;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
}) => {
  const [visId] = useState<string>(() => aguid());
  useRowHighlighter({
    gridApi,
    drillField: gadget.groupByField,
    visId,
  });
  const isCompatibleComparison = useIsCompatibleComparison(gadget);
  const {
    rowData,
    totalsRowData,
    isLoadingData,
    isError,
    getActualDateRange,
    getAverageDateRange,
    getExcludedDateRangeLabels,
  } = useRowData({
    paceMatrix: gadget,
  });
  const { gridOptions, colDefs } = useGridOptions({
    paceMatrix: gadget,
    getActualDateRange,
    getAverageDateRange,
    getExcludedDateRangeLabels,
  });
  const { onDrillDown } = useChartDrillDowns(
    gadget.groupByField,
    undefined,
    'matrix',
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
    },
    [setGridApi],
  );

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) {
      return;
    }

    gridApi.setGridOption('columnDefs', colDefs);
  }, [colDefs, gridApi]);

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) {
      return;
    }

    gridApi.setGridOption('loading', isLoadingData);
  }, [gridApi, isLoadingData]);

  return {
    isCompatibleComparison,
    isLoadingData,
    isError,
    gridOptions,
    colDefs,
    rowData,
    totalsRowData,
    visId,
    onDrillDown,
    onGridReady,
    isCardMode: !!gadget.isCardMode,
    getActualDateRange,
    getAverageDateRange,
    getExcludedDateRangeLabels,
    paceMatrix: gadget,
  };
};

export default usePaceMatrixContent;
